import { create } from 'zustand';

export type ScheduleMode = 'now' | 'later' | 'ai';

export interface MediaAttachment {
  id: string;
  url: string;
  type: 'image' | 'video';
  name: string;
  size: number;
  /** Made by the app's AI: labelled AI-generated on TikTok and YouTube. */
  aiGenerated?: boolean;
  /**
   * What is known of the file, for each channel's own limits (shape, size,
   * format, length; shared-validators media-specs.ts). Absent = not known
   * yet, and not judged on it.
   */
  mimeType?: string;
  width?: number;
  height?: number;
  durationSeconds?: number;
  /** Alt text: read by screen readers on Instagram, X, LinkedIn, Facebook and Pinterest. */
  altText?: string;
}

/** An AI video render in flight. Kept alongside media so the MEDIA strip can
 *  show a "rendering" tile without blocking the rest of composing. */
export interface PendingVideo {
  jobId: string;
  prompt: string;
}

export interface PlatformSelection {
  platform: string;
  postTypes: Set<string>;
}

/** Per-platform caption overrides, keyed by the API platform name (INSTAGRAM, …). */
export type CaptionOverrides = Record<string, string>;

/**
 * What a channel needs beyond the caption, keyed by API platform name:
 * a Google Business post's button and an offer's dates. Empty values are
 * dropped rather than stored.
 */
export type PlatformOptions = Record<string, Record<string, string>>;

/**
 * What the composer held when it was last left: its brand, the draft it was
 * editing (if saved) and its attached media. The media library's "Insert"
 * reads it to add a file to that post rather than to a new, empty one.
 */
export interface ComposerSession {
  workspaceId: string | null;
  draftId: string | null;
  media: MediaAttachment[];
}

/**
 * Router state the media library hands the composer: files to add to the
 * post once it has loaded (a reset or a draft load would wipe a store write
 * made before navigating).
 */
export interface ComposerInsertState {
  insertMedia: MediaAttachment[];
}

/** The insert hand-off in a router location's state, or null. */
export function insertedMediaOf(state: unknown): MediaAttachment[] | null {
  if (!state || typeof state !== 'object' || !('insertMedia' in state)) return null;
  const list = (state as { insertMedia: unknown }).insertMedia;
  return Array.isArray(list) ? (list as MediaAttachment[]) : null;
}

/**
 * A moment as an ISO instant (`…Z`), or null when it is not a time.
 *
 * Hand-offs into the composer (an occasion's post, a weekly-plan item) carry
 * local wall-clock strings such as `2026-11-02T10:00:00`. Sent as they are,
 * the API reads them on a UTC server and the post goes out hours off, so the
 * store only ever holds the instant, read in the browser's own time zone.
 */
export function toInstant(value: string | null | undefined): string | null {
  if (!value) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

interface ComposerState {
  /** The post caption / body text, shared by every platform without an override. */
  caption: string;
  /** Optional per-platform captions (blueprint S21 per-platform tabs). Absent = use `caption`. */
  captionOverrides: CaptionOverrides;
  /** Selected platforms with their chosen post types */
  platforms: Map<string, Set<string>>;
  /** Per-channel extras (Google Business button, offer and event details). */
  platformOptions: PlatformOptions;
  /** Schedule mode */
  scheduleMode: ScheduleMode;
  /** Scheduled datetime (ISO string) for 'later' mode */
  scheduledAt: string | null;
  /** Route the post through an approver before it publishes (teams, any plan). */
  askApprover: boolean;
  /** Media attachments */
  media: MediaAttachment[];
  /** AI video renders currently in progress */
  pendingVideos: PendingVideo[];
  /** A failed render's prompt the user asked to try again: the video sheet opens with it (PC-46). */
  videoRetryPrompt: string | null;
  /** The occasion the post is being made for, when it was opened from one (PC-36). */
  occasionId: string | null;
  /** Whether the AI modal is open */
  aiModalOpen: boolean;
  /** Draft ID if editing an existing draft */
  draftId: string | null;
  /** Whether the post is being published */
  isPublishing: boolean;
  /** The composer as it was last left; survives `reset`. */
  lastSession: ComposerSession | null;

  // Actions
  setCaption: (caption: string) => void;
  /** Pass null to drop the override and fall back to the shared caption. */
  setCaptionOverride: (platform: string, caption: string | null) => void;
  /**
   * Picks the one post type a platform publishes as. The API takes one type
   * per platform, so a second type is never added beside the first.
   */
  setPlatformType: (platform: string, postType: string) => void;
  /** Sets one extra for one channel; an empty value removes it. */
  setPlatformOption: (platform: string, key: string, value: string) => void;
  /** Turns a platform on with a default type, or off entirely. */
  setPlatformEnabled: (platform: string, enabled: boolean, defaultType: string) => void;
  setScheduleMode: (mode: ScheduleMode) => void;
  setScheduledAt: (dt: string | null) => void;
  setAskApprover: (ask: boolean) => void;
  /** Adds a file; one already attached (same id) is not added twice. */
  addMedia: (media: MediaAttachment) => void;
  removeMedia: (id: string) => void;
  /** Moves an attachment to another place: the carousel's order is the post's (F42). */
  moveMedia: (id: string, toIndex: number) => void;
  /**
   * Puts `media` where the attachment `id` is, keeping the order. Does
   * nothing when `id` is no longer attached (removed while it uploaded).
   */
  replaceMedia: (id: string, media: MediaAttachment) => void;
  /** Drops every attachment and AI render in flight (another brand's files). */
  clearMedia: () => void;
  setLastSession: (session: ComposerSession | null) => void;
  addPendingVideo: (video: PendingVideo) => void;
  removePendingVideo: (jobId: string) => void;
  setVideoRetryPrompt: (prompt: string | null) => void;
  setAiModalOpen: (open: boolean) => void;
  setDraftId: (id: string | null) => void;
  setIsPublishing: (publishing: boolean) => void;
  loadDraft: (draft: {
    caption: string;
    platforms: Map<string, Set<string>>;
    media: MediaAttachment[];
    scheduledAt: string | null;
    captionOverrides?: CaptionOverrides;
    platformOptions?: PlatformOptions;
    occasionId?: string | null;
  }) => void;
  reset: () => void;
}

export const useComposerStore = create<ComposerState>((set) => ({
  caption: '',
  captionOverrides: {},
  platforms: new Map(),
  platformOptions: {},
  scheduleMode: 'now',
  scheduledAt: null,
  askApprover: false,
  media: [],
  pendingVideos: [],
  videoRetryPrompt: null,
  occasionId: null,
  aiModalOpen: false,
  draftId: null,
  isPublishing: false,
  lastSession: null,

  setCaption: (caption) => set({ caption }),

  setCaptionOverride: (platform, caption) =>
    set((state) => {
      const captionOverrides = { ...state.captionOverrides };
      if (caption === null) delete captionOverrides[platform];
      else captionOverrides[platform] = caption;
      return { captionOverrides };
    }),

  setPlatformType: (platform, postType) =>
    set((state) => {
      const platforms = new Map(state.platforms);
      platforms.set(platform, new Set([postType]));
      return { platforms };
    }),

  setPlatformOption: (platform, key, value) =>
    set((state) => {
      const own = { ...(state.platformOptions[platform] ?? {}) };
      if (value.trim()) own[key] = value;
      else delete own[key];
      const platformOptions = { ...state.platformOptions };
      if (Object.keys(own).length > 0) platformOptions[platform] = own;
      else delete platformOptions[platform];
      return { platformOptions };
    }),

  setPlatformEnabled: (platform, enabled, defaultType) =>
    set((state) => {
      const platforms = new Map(state.platforms);
      if (enabled) {
        if (!(platforms.get(platform)?.size ?? 0)) platforms.set(platform, new Set([defaultType]));
      } else {
        platforms.delete(platform);
      }
      return { platforms };
    }),

  setScheduleMode: (scheduleMode) => set({ scheduleMode }),
  setScheduledAt: (scheduledAt) => set({ scheduledAt: toInstant(scheduledAt) }),
  setAskApprover: (askApprover) => set({ askApprover }),

  addMedia: (media) =>
    set((state) => (state.media.some((m) => m.id === media.id) ? state : { media: [...state.media, media] })),

  removeMedia: (id) =>
    set((state) => ({ media: state.media.filter((m) => m.id !== id) })),

  moveMedia: (id, toIndex) =>
    set((state) => {
      const from = state.media.findIndex((m) => m.id === id);
      const to = Math.max(0, Math.min(state.media.length - 1, toIndex));
      if (from === -1 || from === to) return state;
      const media = [...state.media];
      const [moved] = media.splice(from, 1);
      if (!moved) return state;
      media.splice(to, 0, moved);
      return { media };
    }),

  replaceMedia: (id, media) =>
    set((state) => {
      const at = state.media.findIndex((m) => m.id === id);
      if (at === -1) return state;
      // The same file already attached elsewhere: keep that one, drop this.
      if (media.id !== id && state.media.some((m) => m.id === media.id)) {
        return { media: state.media.filter((m) => m.id !== id) };
      }
      const next = [...state.media];
      next[at] = media;
      return { media: next };
    }),

  clearMedia: () => set({ media: [], pendingVideos: [] }),
  setLastSession: (lastSession) => set({ lastSession }),

  addPendingVideo: (video) =>
    set((state) => ({ pendingVideos: [...state.pendingVideos, video] })),

  removePendingVideo: (jobId) =>
    set((state) => ({ pendingVideos: state.pendingVideos.filter((v) => v.jobId !== jobId) })),

  setVideoRetryPrompt: (videoRetryPrompt) => set({ videoRetryPrompt }),

  setAiModalOpen: (aiModalOpen) => set({ aiModalOpen }),
  setDraftId: (draftId) => set({ draftId }),
  setIsPublishing: (isPublishing) => set({ isPublishing }),

  loadDraft: (draft) => {
    const scheduledAt = toInstant(draft.scheduledAt);
    // One type per platform: the API keeps one, so a draft never shows more.
    const platforms = new Map(
      Array.from(draft.platforms, ([name, types]) => [name, new Set(Array.from(types).slice(0, 1))] as const),
    );
    set({
      caption: draft.caption,
      captionOverrides: draft.captionOverrides ?? {},
      platforms,
      platformOptions: draft.platformOptions ?? {},
      media: draft.media.filter((m, i, all) => all.findIndex((o) => o.id === m.id) === i),
      scheduledAt,
      scheduleMode: scheduledAt ? 'later' : 'now',
      occasionId: draft.occasionId ?? null,
    });
  },

  reset: () =>
    set({
      caption: '',
      captionOverrides: {},
      platforms: new Map(),
      platformOptions: {},
      scheduleMode: 'now',
      scheduledAt: null,
      askApprover: false,
      media: [],
      pendingVideos: [],
      videoRetryPrompt: null,
      occasionId: null,
      aiModalOpen: false,
      draftId: null,
      isPublishing: false,
    }),
}));
